import React, { useState, useEffect, useCallback } from 'react';
import { RotateCw } from 'lucide-react';
import { authService } from '../../services/authService';
import { Button, Card, Dot } from '../ui';
import { AUTH_BUTTON, AuthActions, AuthScreen, AuthSplash, FormNotice } from './AuthShell';

interface Extension {
  name: string;
  description: string;
}

interface DatabaseStatusData {
  database: {
    ready: boolean;
    issues: string[];
    extensions: {
      allInstalled: boolean;
      missing: Extension[];
      installed: Extension[];
    };
  };
  superuser: {
    exists: boolean;
    requiresSetup: boolean;
  };
  overall: {
    ready: boolean;
    requiresSetup: boolean;
  };
}

interface DatabaseStatusProps {
  onReady: () => void;
  onSetupRequired: () => void;
}

export const DatabaseStatus: React.FC<DatabaseStatusProps> = ({ onReady, onSetupRequired }) => {
  const [status, setStatus] = useState<DatabaseStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkDatabaseStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dbStatus = await authService.getDatabaseStatus();
      setStatus(dbStatus);
      
      if (dbStatus.overall.ready) {
        onReady();
      } else if (dbStatus.overall.requiresSetup) {
        onSetupRequired();
      }
    } catch (err: any) {
      console.error('Failed to check database status:', err);
      setError(err?.response?.data?.message || 'Failed to check database status');
    } finally {
      setLoading(false);
    }
  }, [onReady, onSetupRequired]);

  useEffect(() => {
    checkDatabaseStatus();
  }, [checkDatabaseStatus]);

  if (loading) {
    return <AuthSplash label="Initializing database" />;
  }

  if (error) {
    return (
      <AuthScreen title="Database error">
        <FormNotice>{error}</FormNotice>
        <AuthActions>
          <Button variant="primary" onClick={checkDatabaseStatus} className={AUTH_BUTTON}>
            <RotateCw className="h-4 w-4" strokeWidth={1.75} />
            Retry
          </Button>
        </AuthActions>
      </AuthScreen>
    );
  }

  if (!status) {
    return null;
  }

  const { extensions, issues } = status.database;

  // Show database status details
  return (
    <AuthScreen title="System status">
      <Card divided>
        <StatusRow
          label="Database"
          tone={status.database.ready ? 'green' : 'red'}
          value={status.database.ready ? 'Connected' : 'Connection failed'}
        />
        <StatusRow
          label="Extensions"
          tone={extensions.allInstalled ? 'green' : 'amber'}
          value={extensions.allInstalled ? 'Installed' : 'Installing…'}
        />
        {!extensions.allInstalled &&
          extensions.missing.map((ext: Extension, index: number) => (
            <div key={index} className="flex items-center gap-2.5 py-2 pl-8 pr-4" title={ext.description}>
              <span className="truncate font-ui-mono text-[12px] text-ink">{ext.name}</span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-neutral-400">{ext.description}</span>
            </div>
          ))}
        {issues.map((issue: string, index: number) => (
          <div key={index} className="flex items-start gap-2.5 px-4 py-2.5">
            <Dot tone="red" className="mt-[7px]" />
            <span className="text-[13px] text-primary-700">{issue}</span>
          </div>
        ))}
      </Card>

      <AuthActions>
        <Button variant="secondary" onClick={checkDatabaseStatus} className={AUTH_BUTTON}>
          <RotateCw className="h-4 w-4" strokeWidth={1.75} />
          Refresh
        </Button>
      </AuthActions>
    </AuthScreen>
  );
};

const StatusRow: React.FC<{ label: string; value: string; tone: 'green' | 'amber' | 'red' }> = ({
  label,
  value,
  tone,
}) => (
  <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px]">
    <span className="text-neutral-500">{label}</span>
    <span className="inline-flex items-center gap-2 text-ink">
      <Dot tone={tone} />
      {value}
    </span>
  </div>
);
